//
// ============================================================================
// IMPORTS
// ============================================================================
//

// Types
import type { PendingSample } from "../../../services/api/domains/pending/pending.types";
import type { useValidationFilters } from "./useValidationFilters";
import type { useValidationSelection } from "./useValidationSelection";

//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Validation Refresh Composable
 *
 * Handles manual refresh with leading-edge debounce and selection restoration.
 *
 * Responsibilities:
 * - Manual refresh triggered by user (refresh button)
 * - Abort all pending API requests before refresh
 * - Clear all cached data for fresh fetch
 * - Restore previous selection after refresh
 * - Prevent spam clicks with leading-edge debounce
 * - Handle missing samples gracefully (select next available)
 *
 * @param key - Unique key for state isolation
 * @param pendingComposable - Pending data source
 * @param lisComposable - LIS data source
 * @param filtersComposable - Active filters
 * @param selectionComposable - Selection state
 * @param fetchSamples - Fetch samples method
 * @param fetchSampleData - Fetch sample data method
 * @param selectSample - Select sample method
 * @param filteredSamples - Filtered samples array
 */
export const useValidationRefresh = (
  key: string,
  pendingComposable: ReturnType<typeof usePending>,
  lisComposable: ReturnType<typeof useLis>,
  filtersComposable: ReturnType<typeof useValidationFilters>,
  selectionComposable: ReturnType<typeof useValidationSelection>,
  fetchSamples: (forceRefresh?: boolean, silent?: boolean) => Promise<void>,
  fetchSampleData: (barcode: string, forceRefresh?: boolean) => Promise<void>,
  selectSample: (sample: PendingSample | null, forceRefresh?: boolean) => Promise<void>,
  filteredSamples: ComputedRef<PendingSample[]>,
) => {
  //
  // ============================================================================
  // STATE
  // ============================================================================
  //

  /**
   * Refresh in progress flag
   *
   * Purpose:
   * - Disable refresh button during operation
   * - Prevent concurrent refresh operations
   * - Show loading indicator to user
   */
  const isRefreshing = ref(false);

  /**
   * Timestamp of last refresh (for debounce)
   *
   * Purpose:
   * - Track when last refresh occurred
   * - Calculate time since last refresh
   * - Enforce 500ms cooldown period
   */
  let lastRefreshTime = 0;

  //
  // ============================================================================
  // PRIVATE HELPERS
  // ============================================================================
  //

  /**
   * Abort all pending API requests
   *
   * Purpose:
   * - Cancel in-flight requests before refresh
   * - Prevent stale responses from updating UI
   * - Clean up AbortController references
   *
   * Responsibilities:
   * - Call abortAll on pending composable
   * - Call abortAll on LIS composable
   * - Wait for next tick (DOM update cycle)
   */
  const abortAllRequests = (): void => {
    pendingComposable.abortAll();
    lisComposable.abortAll();
  };

  /**
   * Clear all cached data
   *
   * Purpose:
   * - Force fresh fetch from backend
   * - Clear stale cached results
   * - Reset cache state for clean slate
   *
   * Responsibilities:
   * - Clear pending samples cache
   * - Clear LIS visit cache
   * - Clear result flagging cache
   */
  const clearAllCaches = (): void => {
    pendingComposable.clearCache();
    lisComposable.clearCache();
    lisComposable.clearFlaggingCache();
  };

  /**
   * Restore previous sample selection after refresh
   *
   * Purpose:
   * - Maintain user's focus after refresh
   * - Find sample with saved barcode in refreshed list
   * - Fallback to first sample if original not found
   *
   * Responsibilities:
   * - Search for sample by barcode in refreshed list
   * - If found: Re-select with forceRefresh flag
   * - If not found: Select first filtered sample
   * - If no samples: Clear selection gracefully
   *
   * @param barcode - Barcode of sample to restore
   */
  const restoreSelection = async (barcode: string): Promise<void> => {
    // Find sample in refreshed list
    const restoredSample = pendingComposable.samples.value.find((s) => s.barcode === barcode);

    if (restoredSample) {
      // Sample still exists - re-select with force refresh
      await selectSample(restoredSample, true);
    } else {
      // Sample no longer exists - select first available
      if (filteredSamples.value[0]) {
        await selectSample(filteredSamples.value[0]);
      } else {
        selectionComposable.clearSelection();
      }
    }
  };

  //
  // ============================================================================
  // PUBLIC API
  // ============================================================================
  //

  /**
   * Refresh all data with leading-edge debounce
   *
   * Purpose:
   * - Provide manual refresh triggered by user action
   * - Fetch fresh data from backend (bypass cache)
   * - Maintain user's selection focus after refresh
   * - Prevent spam clicks and race conditions
   *
   * Responsibilities:
   * - Check if already refreshing (block if true)
   * - Enforce 500ms cooldown between refreshes
   * - Save current selection for restoration
   * - Abort all pending requests
   * - Clear all caches (pending + LIS)
   * - Fetch fresh samples with current filters
   * - Restore previous selection (or select first)
   * - Handle errors gracefully
   * - Clear refreshing flag in finally block
   */
  const refresh = async (): Promise<void> => {
    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefreshTime;

    // Block if already refreshing
    if (isRefreshing.value) {
      return;
    }

    // Block rapid clicks (500ms cooldown)
    if (timeSinceLastRefresh < 500) {
      return;
    }

    // Execute immediately
    lastRefreshTime = now;
    isRefreshing.value = true;

    try {
      // Save current selection for restoration
      const currentBarcode = selectionComposable.selectedSample.value?.barcode;

      // Clean slate: abort requests and clear caches
      abortAllRequests();
      clearAllCaches();

      // Fetch fresh data
      await fetchSamples(true);

      // Restore selection
      if (currentBarcode) {
        await restoreSelection(currentBarcode);
      } else if (filteredSamples.value[0]) {
        await selectSample(filteredSamples.value[0]);
      }
    } catch (error) {
      // Refresh error - already handled by fetchSamples composable
      // Error state available via pendingComposable.error
    } finally {
      isRefreshing.value = false;
    }
  };

  //
  // ============================================================================
  // PUBLIC API
  // ============================================================================
  //

  return {
    // State
    isRefreshing,

    // Methods
    refresh,
  };
};
